import { useEffect, useId, useRef, useState } from "react";
import { ArrowUpRight, ChevronDown, ChevronRight, ClipboardList, FileText, Package, UserRoundCheck, Wrench, X } from "lucide-react";
import type { Schema, Work } from "./api";
import { SourceReferences } from "./SourcePicker";
import { decimalText } from "./resourcePrice";
import { resourcePresentation, resourceWorkIds } from "./resourcePresentation";
import "./resource-actions.css";

type Resource = Schema["ResourceLine"];
type Props = {
  resources: Resource[];
  revisionId: string;
  availableWorks: Work[];
  openWork: (id: string) => void;
  openSource: (id: string, key?: string) => void;
  /** Where the ЛСР prices each work, by work id. */
  places?: Record<string, string>;
};

function quantityText(resource: Resource) {
  return resource.quantity == null || resource.quantity.trim() === ""
    ? "Количество не установлено"
    : `${decimalText(resource.quantity)} ${resource.unit}`.trim();
}

function WorkLinks({ resource, availableWorks, openWork }: Pick<Props, "availableWorks" | "openWork"> & { resource: Resource }) {
  const ids = resourceWorkIds(resource);
  return <ul className="ra-work-links">{ids.map((id, index) => <li key={id}>
    <button type="button" onClick={() => openWork(id)}>
      <span>{availableWorks.find(work => work.id === id)?.description || (ids.length === 1 && id === resource.work_id ? resource.work : "") || `Связанная работа ${index + 1}`}</span>
      <ArrowUpRight size={15} aria-hidden="true" />
    </button>
  </li>)}</ul>;
}

function ResourceEvidence({ resource, revisionId, openSource, places = {} }: Pick<Props, "revisionId" | "openSource" | "places"> & { resource: Resource }) {
  const view = resourcePresentation(resource);
  const lsr = [...new Set(resourceWorkIds(resource).map(id => places[id]).filter(Boolean))].join("; ");
  return <>
    <dl className="ra-facts">
      <div><dt>Количество ресурса</dt><dd>{quantityText(resource)}</dd></div>
      {lsr && <div><dt>В ЛСР</dt><dd>{lsr}</dd></div>}
      <div><dt>Проверка цены</dt><dd>{view.priceLabel}</dd></div>
      {view.price && <div><dt>Цена и основание</dt><dd>{view.price}</dd></div>}
      {resource.catalog_code && <div><dt>Код в каталоге</dt><dd>{resource.catalog_code}</dd></div>}
      {view.equipmentLabel && <div><dt>Комплектность</dt><dd>{view.equipmentLabel}</dd></div>}
      {!!resource.placements?.length && <div><dt>Места применения</dt><dd>{resource.placements.join(" · ")}</dd></div>}
    </dl>
    {resource.specification && <section><h3>Характеристики</h3><p>{resource.specification}</p></section>}
    {resource.note && <section><h3>Примечание из данных проекта</h3><p>{resource.note}</p><small>Примечание может относиться к предыдущему анализу. Текущее состояние показано отдельно.</small></section>}
    {resource.price_reason && !resource.note.includes(resource.price_reason) && <section><h3>Основание состояния цены</h3><p>{resource.price_reason}</p></section>}
    {resource.search_evidence && <section><h3>Основание проверки цены</h3><p>{resource.search_evidence}</p></section>}
    {resource.equipment_evidence && <section><h3>Основание проверки комплектации</h3><p>{resource.equipment_evidence.replace(/^eom-equipment-v\d+:\s*/, "")}</p></section>}
    {resource.manufacturer_url && <p><a href={resource.manufacturer_url} target="_blank" rel="noreferrer">Источник изготовителя <ArrowUpRight size={13} aria-hidden="true" /></a></p>}
    <section><h3>Источники ресурса</h3><SourceReferences revisionId={revisionId} value={resource.source_ids} openSource={openSource} /></section>
  </>;
}

/** Presentation only: each server ResourceLine remains one card, with its own quantity and links. */
export function ResourceActionView({ resources, revisionId, availableWorks, openWork, openSource, places = {} }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const details = resources.find(resource => resource.id === detailsId);
  const dialog = useRef<HTMLDialogElement>(null);
  const prefix = useId();
  useEffect(() => {
    if (details) dialog.current?.showModal();
    else dialog.current?.close();
  }, [details]);
  const close = () => { dialog.current?.close(); setDetailsId(null); };
  const toggle = (id: string) => setExpanded(current => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return <>
    <div className="resource-actions">
      {resources.length > 0 && <div className="ra-toolbar"><span>Ресурс → вопросы и действия → основания</span><button type="button" onClick={() => setExpanded(new Set())} disabled={!expanded.size}>Свернуть всё</button></div>}
      {resources.map(resource => {
        const view = resourcePresentation(resource);
        const isOpen = expanded.has(resource.id);
        const panelId = `${prefix}-${encodeURIComponent(resource.id)}`;
        const workIds = resourceWorkIds(resource);
        const lsr = [...new Set(workIds.map(id => places[id]).filter(Boolean))].join("; ");
        return <section className={`ra-entity${isOpen ? " is-open" : ""}`} key={resource.id} aria-label={`Ресурс: ${resource.name}`}>
          <div className="ra-heading">
            <button type="button" className="ra-toggle" aria-expanded={isOpen} aria-controls={isOpen ? panelId : undefined} onClick={() => toggle(resource.id)}>
              <span className="ra-symbol"><Package size={24} aria-hidden="true" /></span>
              <span className="ra-title"><strong>{resource.name}</strong><span>{view.actions.length ? view.actions.map(action => action.action).join(" · ") : "Нет уточнений по текущим статусам"}</span></span>
              <span className="ra-overview"><strong>{quantityText(resource)}</strong><span className={`ra-price-state ra-price-state--${resource.price_status === "found" && !view.price ? "specification_missing" : resource.price_status}`}>{view.priceLabel}</span>{lsr && <span className="ra-lsr">Работа оценена в {lsr}</span>}</span>
              <ChevronDown size={18} className="ra-chevron" aria-hidden="true" />
            </button>
            <button type="button" className="ra-more" aria-label={`Подробнее: ${resource.name}`} onClick={() => setDetailsId(resource.id)}>Подробнее</button>
          </div>
          {isOpen && <div id={panelId}>
            {!!workIds.length && <details className="ra-linked"><summary><Wrench size={14} aria-hidden="true" />Связанные работы · {workIds.length}<ChevronDown size={14} aria-hidden="true" /></summary><WorkLinks resource={resource} availableWorks={availableWorks} openWork={openWork} />{workIds.length > 1 && <p>Одна потребность связана с несколькими работами. Количество показано один раз, без повторного сложения.</p>}</details>}
            <h3 className="ra-section-title"><ClipboardList size={15} aria-hidden="true" />Что нужно сделать <span>{view.actions.length}</span></h3>
            {view.actions.length ? view.actions.map(action => <details className={`ra-question ra-question--${action.tone}`} key={action.key}>
              <summary><ChevronRight size={16} className="ra-question-chevron" aria-hidden="true" /><span className="ra-question-text"><span className="ra-topic">{action.topic}</span><strong>{action.title}</strong><span className="ra-action"><UserRoundCheck size={14} aria-hidden="true" /><span>Что сделать: <b>{action.action}</b></span></span></span></summary>
              <div className="ra-question-detail">
                {action.evidence.map((text, index) => <p key={index}>{text}</p>)}
                {workIds.length > 0 && <><p className="ra-help">Проверить и уточнить данные можно в сведениях связанной работы. Открытие работы ничего не подтверждает.</p><WorkLinks resource={resource} availableWorks={availableWorks} openWork={openWork} /></>}
                <button type="button" className="ra-evidence-button" onClick={() => setDetailsId(resource.id)}><FileText size={14} aria-hidden="true" />Характеристики и источники</button>
              </div>
            </details>) : <p className="ra-no-actions">По текущим статусам уточнения не выделены. Это не означает, что все данные проверены.</p>}
            {view.price && <p className="ra-found-price"><span>Найденная цена</span><strong>{view.price}</strong></p>}
            {view.equipmentLabel && !view.actions.some(action => action.key === "equipment") && <p className="ra-equipment-state">Комплектность: {view.equipmentLabel}</p>}
            <div className="ra-footer"><button type="button" onClick={() => setDetailsId(resource.id)}><FileText size={14} aria-hidden="true" />Характеристики, примечания и источники</button></div>
          </div>}
        </section>;
      })}
    </div>
    <dialog ref={dialog} className="resource-actions-dialog" aria-labelledby={`${prefix}-detail-title`} onClose={() => setDetailsId(null)} onClick={event => { if (event.target === event.currentTarget) close(); }}>
      {details && <div className="ra-dialog-content"><div className="ra-dialog-heading"><div><small>Сведения о ресурсе</small><h2 id={`${prefix}-detail-title`}>{details.name}</h2></div><button type="button" aria-label="Закрыть сведения о ресурсе" onClick={close}><X size={20} aria-hidden="true" /></button></div>
        <ResourceEvidence resource={details} revisionId={revisionId} places={places} openSource={(id, key) => { close(); openSource(id, key); }} />
        {!!resourceWorkIds(details).length && <section><h3>Связанные работы</h3><WorkLinks resource={details} availableWorks={availableWorks} openWork={id => { close(); openWork(id); }} /></section>}
      </div>}
    </dialog>
  </>;
}
